import React, { useCallback, useEffect, useMemo, useState } from 'react';
import reportsService from '../service/reportsService.mjs';
import { useFiscalReport } from '../hook/useFiscalReport.mjs';
import { money, percent } from '../util/format.mjs';
import { regionsOf, inRegion, inQuarter, fundingByCode } from '../util/aggregate.mjs';
import { periodText as periodTextOf, periodTag, namedRegion } from '../util/period.mjs';
import { exportFunding } from '../util/reportsExcel.mjs';
import {
  ScopeToolbar, StatTiles, LoadingCard, ErrorCard, Section, EmptyRow, TH, THR, TD, TDR,
} from './ReportParts.mjs';

/**
 * What each PTAE paid for: the fiscal year's finally approved reports, one
 * funding line per split (or per unsplit claim), totalled by code. The same
 * rule as the signed report's FUNDING BY PTAE table, so the two agree.
 *
 * The region goes to the server, which clamps it to the caller's scope; the
 * quarter is narrowed here from the year's lines.
 */
const FundingTab = ({ scope }) => {
  const { fy, quarter, region, addRegions } = scope;
  const load = useCallback(() => reportsService.getFunding({ fy, region }), [fy, region]);
  const { data, loading, error, fiscalYears, reload } = useFiscalReport(load, fy);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const all = useMemo(() => data?.lines || [], [data]);
  // Filtered here too, so the table never shows a region other than the one
  // picked, whatever the server sent.
  const lines = useMemo(() => inQuarter(inRegion(all, region), quarter), [all, region, quarter]);
  const codes = useMemo(() => fundingByCode(lines, data?.ptaes || []), [lines, data]);

  useEffect(() => {
    addRegions([...regionsOf(all), ...(data?.regions || [])]);
  }, [all, data, addRegions]);

  if (!data && loading) return <LoadingCard text="Loading funding..." />;
  if (error && !data) return <ErrorCard error={`Could not load funding: ${error}`} onRetry={reload} />;

  const periodText = periodTextOf(fy, quarter);
  const funded = codes.filter(c => c.code);
  const unassigned = codes.find(c => !c.code);
  const grand = codes.reduce((t, c) => t + c.amount, 0);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await exportFunding({
        lines, ptaes: data?.ptaes || [], fyLabel: periodText,
        fyShort: periodTag(fy, quarter),
        // Someone who sees one region has no picker; the file still names it.
        region: namedRegion(scope, data?.region || (regionsOf(all).length === 1 ? regionsOf(all)[0] : null)),
      });
    } catch (err) {
      setExportError(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <ScopeToolbar
        scope={scope}
        fiscalYears={fiscalYears}
        onExport={handleExport}
        exporting={exporting}
        loading={loading}
        note="Finally approved reports only. A claim split across several PTAEs counts once under each of them, with that PTAE's part."
      />
      {(error || exportError) && (
        <div className="mb-4"><ErrorCard error={exportError || `Could not refresh: ${error}`} /></div>
      )}

      <StatTiles tiles={[
        { label: 'Funded', value: money(grand - (unassigned?.amount || 0)), hint: `${funded.length} PTAE code${funded.length === 1 ? '' : 's'} used` },
        {
          label: 'No PTAE assigned',
          value: money(unassigned?.amount || 0),
          hint: unassigned
            ? `${unassigned.claims} claim${unassigned.claims === 1 ? ' needs' : 's need'} a code`
            : 'Every approved line has a code',
          tone: unassigned ? 'warn' : undefined,
        },
        { label: 'Total', value: money(grand), hint: periodText },
      ]} />

      <Section title="Funding by PTAE" subtitle="Largest first. Money with no PTAE is listed last.">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stanford-red text-white">
              <tr>
                <th className={TH}>PTAE</th>
                <th className={TH}>Description</th>
                <th className={TH}>Region</th>
                <th className={THR}>Claims</th>
                <th className={THR}>Amount</th>
                <th className={THR}>Share</th>
              </tr>
            </thead>
            <tbody>
              {codes.map(c => (
                <tr
                  key={c.code || '~none'}
                  className={`border-b border-gray-100 ${c.code ? 'hover:bg-gray-50' : 'bg-amber-50'}`}
                >
                  <td className={`${TD} font-mono text-gray-800 break-all`}>
                    {c.code || <span className="font-sans font-semibold text-amber-800">Not assigned</span>}
                    {c.suspended && (
                      <span className="ml-2 font-sans inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                        Suspended
                      </span>
                    )}
                  </td>
                  <td className={TD}>
                    {c.description || (c.code ? <span className="text-gray-400">Not in the PTAE list</span> : '')}
                    {c.fundName && <div className="text-xs text-gray-500">{c.fundName}</div>}
                  </td>
                  <td className={TD}>{c.region}</td>
                  <td className={TDR}>{c.claims}</td>
                  <td className={`${TDR} font-medium text-gray-800`}>{money(c.amount)}</td>
                  <td className={TDR}>{percent(c.amount, grand)}</td>
                </tr>
              ))}
              {codes.length === 0 && <EmptyRow colSpan={6} text={`No finally approved reports in ${periodText}.`} />}
            </tbody>
            {codes.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td className={TD} colSpan={4}>Total</td>
                  <td className={TDR}>{money(grand)}</td>
                  <td className={TDR}>100%</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Section>
    </>
  );
};

export default FundingTab;
